import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, Envelope, Phone, Lock, ArrowLeft, Gift, Cake, ShieldCheck, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Onboarding from "@/components/Onboarding";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();
  const [tab, setTab] = useState(searchParams.get("tab") || "login");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", password: "", referral_code: searchParams.get("ref") || "", birth_date: "" });
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Email verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // If already logged in, redirect
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Reset verification when email changes
  const handleEmailChange = (email) => {
    setFormData(prev => ({ ...prev, email }));
    if (emailVerified || codeSent) {
      setEmailVerified(false);
      setCodeSent(false);
      setVerificationCode("");
    }
  };

  const handleSendCode = async () => {
    if (!formData.email) { toast.error("Digite seu email"); return; }
    setSendingCode(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/send-verification`, { email: formData.email });
      setCodeSent(true);
      setCountdown(60);
      toast.success("Código enviado para seu email!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar código");
    } finally { setSendingCode(false); }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 6) { toast.error("Digite o código de 6 dígitos"); return; }
    setVerifying(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-code`, { email: formData.email, code: verificationCode });
      setEmailVerified(true);
      toast.success("Email verificado com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorreto");
    } finally { setVerifying(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Login realizado!");
      navigate("/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.phone, formData.password, formData.birth_date, formData.referral_code);
      toast.success("Cadastro realizado com sucesso!");

      // Send WhatsApp notification
      const adminWhatsApp = "5521984317457";
      const msg = `🎉 *NOVO CADASTRO - MARABU OPEN CLUB*\n\n👤 *Nome:* ${formData.name}\n📧 *Email:* ${formData.email}\n📱 *Telefone:* ${formData.phone}\n🕐 *Data:* ${new Date().toLocaleString('pt-BR')}\n\n✅ Cliente cadastrado!`;
      window.open(`https://wa.me/${adminWhatsApp}?text=${encodeURIComponent(msg)}`, '_blank');
      // Show onboarding tour
      setShowOnboarding(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao cadastrar");
    } finally {
      setLoading(false);
    }
  };

  const onOnboardingComplete = () => {
    setShowOnboarding(false);
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-6 text-[#115E59]"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={20} className="mr-2" /> Voltar
        </Button>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E4DFD3]">
          <h1 className="text-3xl font-black text-[#042F2E] text-center mb-2">MARABU OPEN CLUB</h1>
          <p className="text-[#115E59] text-center mb-8">Seu clube de lazer e esporte</p>

          {/* Tabs */}
          <div className="flex mb-8 bg-[#F4F1EA] rounded-full p-1">
            <button
              onClick={() => setTab("login")}
              className={`flex-1 py-3 rounded-full text-sm font-bold transition-all ${tab === "login" ? "bg-[#FF5A26] text-white shadow-md" : "text-[#115E59]"}`}
              data-testid="login-tab"
            >
              Entrar
            </button>
            <button
              onClick={() => setTab("register")}
              className={`flex-1 py-3 rounded-full text-sm font-bold transition-all ${tab === "register" ? "bg-[#FF5A26] text-white shadow-md" : "text-[#115E59]"}`}
              data-testid="register-tab"
            >
              Cadastrar
            </button>
          </div>

          {/* Login Form */}
          {tab === "login" && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Email</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-12 rounded-xl border-[#E4DFD3]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="password"
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-12 rounded-xl border-[#E4DFD3]"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg"
                data-testid="login-submit-button"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
              <button
                type="button"
                onClick={() => setTab("forgot")}
                className="w-full text-sm text-[#0891B2] font-semibold hover:underline mt-2"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {tab === "forgot" && (
            <ForgotPasswordForm onBack={() => setTab("login")} />
          )}

          {/* Register Form */}
          {tab === "register" && (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="text"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-12 rounded-xl border-[#E4DFD3]"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              {/* Email with verification */}
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Email</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className={`pl-12 rounded-xl border-[#E4DFD3] ${emailVerified ? "pr-12 border-green-400 bg-green-50" : ""}`}
                    required
                    disabled={emailVerified}
                    data-testid="register-email-input"
                  />
                  {emailVerified && (
                    <ShieldCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" size={22} weight="fill" />
                  )}
                </div>

                {/* Verification flow */}
                {!emailVerified && (
                  <div className="space-y-3">
                    {!codeSent ? (
                      <Button
                        type="button"
                        onClick={handleSendCode}
                        disabled={sendingCode || !formData.email}
                        className="w-full bg-[#042F2E] hover:bg-[#115E59] text-white font-bold rounded-xl py-4 text-sm"
                        data-testid="send-code-btn"
                      >
                        <PaperPlaneTilt size={18} weight="bold" className="mr-2" />
                        {sendingCode ? "Enviando..." : "Enviar Código de Verificação"}
                      </Button>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                        <div className="bg-[#FFF4E6] rounded-xl p-3 text-center">
                          <p className="text-sm text-[#042F2E] font-semibold">Código enviado para <strong>{formData.email}</strong></p>
                          <p className="text-xs text-[#115E59] mt-1">Verifique sua caixa de entrada e spam</p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="000000"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            className="rounded-xl border-[#E4DFD3] text-center text-2xl font-bold tracking-[0.5em]"
                            maxLength={6}
                            data-testid="verification-code-input"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={handleVerifyCode}
                            disabled={verifying || verificationCode.length < 6}
                            className="flex-1 bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-xl py-4 text-sm"
                            data-testid="verify-code-btn"
                          >
                            {verifying ? "Verificando..." : "Verificar"}
                          </Button>
                          <Button
                            type="button"
                            onClick={handleSendCode}
                            disabled={sendingCode || countdown > 0}
                            variant="outline"
                            className="rounded-xl border-[#E4DFD3] text-sm font-bold"
                            data-testid="resend-code-btn"
                          >
                            {countdown > 0 ? `${countdown}s` : "Reenviar"}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {emailVerified && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="flex items-center gap-2 text-xs text-green-600 bg-green-50 rounded-xl p-3">
                    <ShieldCheck size={16} weight="fill" />
                    <span>Email verificado com sucesso!</span>
                  </motion.div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Telefone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-12 rounded-xl border-[#E4DFD3]"
                    required
                    data-testid="register-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#115E59]" size={20} weight="duotone" />
                  <Input
                    type="password"
                    placeholder="Crie uma senha"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-12 rounded-xl border-[#E4DFD3]"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-[#115E59]">Data de Nascimento</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  className="rounded-xl border-[#E4DFD3]"
                  data-testid="register-birthdate-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !emailVerified}
                className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? "Cadastrando..." : "Criar Conta"}
              </Button>
              {formData.referral_code && (
                <div className="flex items-center gap-2 text-xs text-[#10B981] bg-green-50 rounded-xl p-3">
                  <Gift size={16} weight="fill" />
                  <span>Código de indicação: <strong>{formData.referral_code}</strong></span>
                </div>
              )}
              <label className="flex items-start gap-2 text-xs text-[#115E59] cursor-pointer">
                <input type="checkbox" required className="mt-0.5 accent-[#FF5A26]" data-testid="terms-checkbox" />
                <span>Li e aceito a <a href="/privacidade/" target="_blank" className="text-[#FF5A26] underline">Política de Privacidade</a> e os <a href="/termos/" target="_blank" className="text-[#FF5A26] underline">Termos de Uso</a>. Menores de 18 anos devem ter autorização do responsável legal.</span>
              </label>
            </form>
          )}
        </div>

        {/* Onboarding Modal */}
        <AnimatePresence>
          {showOnboarding && <Onboarding onComplete={onOnboardingComplete} />}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// Forgot Password Component
const ForgotPasswordForm = ({ onBack }) => {
  const [step, setStep] = useState("email"); // email, code, done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [codeHint, setCodeHint] = useState("");
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setCodeHint(res.data.code_hint || "");
      toast.success("Código gerado! Validade: 15 minutos.");
      setStep("code");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { email, code, new_password: newPassword });
      toast.success("Senha alterada com sucesso!");
      setStep("done");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="forgot-form">
      {step === "email" && (
        <form onSubmit={requestCode} className="space-y-4">
          <p className="text-sm text-[#115E59] mb-4">Digite seu email para receber um código de recuperação.</p>
          <Input type="email" placeholder="Seu email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-xl border-[#E4DFD3]" required data-testid="forgot-email" />
          <Button type="submit" disabled={loading} className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5"
            data-testid="forgot-submit">{loading ? "Enviando..." : "Enviar Código"}</Button>
          <button type="button" onClick={onBack} className="w-full text-sm text-[#115E59] hover:underline">Voltar ao login</button>
        </form>
      )}
      {step === "code" && (
        <form onSubmit={resetPassword} className="space-y-4">
          <p className="text-sm text-[#115E59] mb-2">Digite o código recebido e sua nova senha.</p>
          {codeHint && <div className="bg-[#FFF4E6] rounded-xl p-3 text-center text-sm"><span className="text-[#115E59]">Código: </span><strong className="text-[#FF5A26] font-mono text-lg">{codeHint}</strong></div>}
          <Input type="text" placeholder="Código de 6 dígitos" value={code} onChange={(e) => setCode(e.target.value)}
            className="rounded-xl border-[#E4DFD3] text-center font-mono text-lg" required data-testid="forgot-code" />
          <Input type="password" placeholder="Nova senha (mín. 6 caracteres)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className="rounded-xl border-[#E4DFD3]" required minLength={6} data-testid="forgot-new-password" />
          <Button type="submit" disabled={loading} className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5"
            data-testid="forgot-reset">{loading ? "Alterando..." : "Alterar Senha"}</Button>
        </form>
      )}
      {step === "done" && (
        <div className="text-center py-6">
          <p className="text-xl font-bold text-[#10B981] mb-3">Senha Alterada!</p>
          <Button onClick={onBack} className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-8">Fazer Login</Button>
        </div>
      )}
    </div>
  );
};

export default Login;
